<template>
  <div
    :class="[
      $style.lvImage,
      $style[`lvImage${type?.cssName}`],
    ]"
    v-bind="awAttrsNoListeners($attrs)"
  >
    <picture
      :class="[
        $style.picture,
        $style[`picture${type?.cssName}`],
        $style[`pictureSizing${sizing}`],
        {
          [$style[`pictureAutoHeight`]]: autoHeight,
        },
      ]"
    >
      <source
        v-for="(source, index) in sources"
        :key="`src-${index}`"
        :media="source.media"
        :type="source.mimeType"
        :srcset="source.url"
      >
      <img
        ref="imgElement"
        v-aw-lazy-media
        :src="defaultImage"
        :alt="createAltTag"
        :loading="loading"
        :class="[$style.image, imgClass]"
        :width="width"
        :height="height"
        v-bind="awListeners($attrs)"
        @load="onImageLoad"
        @error="onImageError"
      >
    </picture>
    <aw-spinner v-if="!isImgLoaded" :class="$style.spinner" />
  </div>
</template>
<script setup>
  import { getCurrentInstance } from 'vue';
  import { awListeners, awAttrsNoListeners } from '~~/common/utils/awListeners';
  import { onImageComplete, removeImageSourceTagOnError } from '~~/common/utils/imageHelper.js';
  import * as LvImageTypes from '~~/common/config/LvImageType';
  import { LV_IMG_TYPE_DEFAULT } from '~~/common/config/LvImageType';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';
  import defaultImage from '~~/common/assets/images/shop-entity-default.jpg';

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    autoHeight: {
      type: Boolean,
      default: false,
    },
    sources: {
      type: Array,
      default: () => [],
    },
    type: {
      type: LV_IMG_TYPE_DEFAULT.constructor,
      default: LV_IMG_TYPE_DEFAULT,
      validator: value => validImageTypes.has(value),
    },
    sizing: {
      default: '',
      type: String,
      validator: value => validImageSizing.has(value),
    },
    alt: {
      type: [Symbol, String],
      default: ALT_EMPTY,
    },
    loading: {
      type: String,
      default: 'lazy',
      validator: value => validLoading.has(value),
    },
    width: {
      type: [String, Number],
      default: null,
    },
    height: {
      type: [String, Number],
      default: null,
    },
    imgClass: {
      type: [String, Array, Object],
      default: null,
    },
  });

  const $emit = defineEmits([
    'image-error',
  ]);

  const instance = getCurrentInstance();
  const imgElement = ref(null);
  const isImgLoaded = ref(props.sources?.length <= 0);
  const createAltTag = computed(() => {
    if (props.alt === ALT_EMPTY) {
      // For now this would be a too aggressive log, maybe later we should turn it on:
      // this.$logger.warn('@deprecated LvImage without an alt attribute. If you don\'t want an alt tag specify it as any empty string', import.meta.client ? this : this.$.uid);
      return '';
    }
    return props.alt;
  });
  watch(() => props.sources, () => {
    instance?.proxy?.$forceUpdate();
  });
  onMounted(() => {
    onImageComplete(
      imgElement.value,
      {
        onImageError,
        onImageLoad,
      },
    );
  });

  function onImageError ({ target }) {
    $emit('image-error');
    if (target) {
      requestAnimationFrame(() => {
        isImgLoaded.value = true;
        removeImageSourceTagOnError(target);
      });
    }
  }

  function onImageLoad ({ target }) {
    if (target) {
      requestAnimationFrame(() => {
        isImgLoaded.value = true;
      });
    }
  }
</script>
<script>
  const validImageTypes = new Set(Object.values(LvImageTypes));
  const validImageSizing = new Set(['', 'cover', 'contain']);
  const validLoading = new Set(['lazy', 'eager']);

  const ALT_EMPTY = Symbol('emptyAltTag');
</script>

<style module lang="scss" rel="stylesheet/scss">
.lvImage {
  --lvImageBg: #{$color-white-transparent};
  position: relative;
  display: inline-flex;
  overflow: hidden;
  width: 100%;
  background-color: var(--lvImageBg);

  &default {
    --lvImageBg: #{$color-white};
  }
}

.spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
}

.picture {
  font-size: 0;
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;

  &AutoHeight {
    .image {
      position: static;
    }
  }

  &default {
    .image {
      object-fit: contain;
      object-position: center center;
    }
  }

  &variant {
    display: block;
    padding-bottom: math.percentage(math.div(1, 1));

    .image {
      object-fit: contain;
      object-position: center center;
    }
  }

  &category {
    display: block;

    .image {
      object-fit: contain;
      object-position: center center;
    }
  }

  &youtube {
    display: block;
    padding-bottom: 56.25%;
  }

  &banner {
    .image {
      object-fit: cover;
    }
  }

  &Sizing {
    &cover {
      .image {
        object-fit: cover;
      }
    }

    &contain {
      .image {
        object-fit: contain;
      }
    }
  }
}

.image {
  position: absolute;
  display: block;
  width: 100%;
  height: 100%;
  transition: opacity 0.256s ease;
}
</style>
